import { format } from 'date-fns';
import { taskAPI } from '@/utils/api';
import { fetchAllCompositeImageRows, mergeCompositeImagesIntoGoodsForms } from './goodsDetailCompositeMerge';
import { runGoodsFormsSequentialComposite } from './goodsSequentialComposite';
import { withIpeProcessingSlot } from './ipeProcessingQueue';
import { publishGoodsTaskProgress } from './goodsTaskProgressBus';
import {
  applyGoodsPrintUrlsToForms,
  buildPersistedGoodsPrintUrlMap,
  collectGoodsPrintUrlsFromForms,
} from './goodsPrintUrlResolve';
import {
  buildGoodsPrintUrlMapFromAssets,
  buildTaskImageAssetsPayload,
} from './imageAssetModel';

/** 避免工作台与详情页重复启动同一任务的合成 */
const processingLocks = new Set();

export function isGoodsTaskProcessingActive(taskId) {
  return processingLocks.has(String(taskId));
}

function buildGoodsImageUrlSnapshot(goodsPrintUrlByImageId) {
  const map = new Map();
  if (!goodsPrintUrlByImageId || typeof goodsPrintUrlByImageId !== 'object') return map;
  Object.entries(goodsPrintUrlByImageId).forEach(([k, v]) => {
    const u = v != null ? String(v).trim() : '';
    if (k && u) map.set(String(k), u);
  });
  return map;
}

/** 从合成图库恢复各组成品，用于续跑时跳过已完成组（分页拉全量，避免只恢复前几组） */
export async function hydrateGoodsFormsFromCompositeStore(taskId, forms) {
  try {
    const rows = await fetchAllCompositeImageRows(taskId);
    if (!rows.length) return forms;
    return mergeCompositeImagesIntoGoodsForms(forms, rows);
  } catch (error) {
    console.warn('[主图创建] 加载已保存合成图失败', error);
    return forms;
  }
}

function countCompletedGroups(forms) {
  let success = 0;
  (forms || []).forEach((form) => {
    const crs = form.compositeResults || [];
    const groupCount = form.imageGroups?.length || 0;
    for (let gi = 0; gi < groupCount; gi += 1) {
      const gr = crs.filter(
        (r) => r.groupIndex === gi && r.compositeUrl && String(r.compositeUrl).trim(),
      );
      if (gr.length > 0) success += 1;
    }
  });
  return success;
}

function toRefsOnlyGoodsForms(partialGoodsForms, taskId) {
  return (partialGoodsForms || []).map((form) => ({
    ...form,
    compositeResults: (form.compositeResults || []).map((result) => {
      if (result.compositeUrl?.startsWith?.('data:image/')) {
        return {
          imageId: result.imageId,
          groupIndex: result.groupIndex,
          compositeImageId: `COMP-${taskId}-${form.templateId}-${result.groupIndex}-${result.imageId}`,
          composeEngine: result.composeEngine,
          fabricInstanceJson: result.fabricInstanceJson,
        };
      }
      return result;
    }),
  }));
}

/**
 * 在后台执行主图创建逐组合成（工作台创建后或详情页续跑）。
 * 进度仅同步 successCount/failedCount，避免超大 goodsForms 导致 update 失败。
 */
export async function runGoodsTaskBackgroundProcessing({
  taskId,
  effectiveGoodsForms,
  imageSpaceImages = [],
  goodsPrintUrlByImageId = {},
  fromCropTaskId,
  taskResultBase = {},
  abortSignal,
  onLocalProgress,
  hydrateExisting = true,
  billingNav,
}) {
  const id = String(taskId);
  if (!id || !effectiveGoodsForms?.length) return null;
  if (processingLocks.has(id)) return null;
  processingLocks.add(id);

  const runCore = async () => {
    const lineageId = String(
      fromCropTaskId
      || taskResultBase?.sourceCropTaskId
      || taskResultBase?.sourceCutoutTaskId
      || '',
    ).trim();
    const persistedPrintMap = buildPersistedGoodsPrintUrlMap(
      {
        ...(taskResultBase?.goodsPrintUrlByImageId || {}),
        ...goodsPrintUrlByImageId,
      },
      lineageId,
    );
    let forms = applyGoodsPrintUrlsToForms(
      effectiveGoodsForms.map((f) => ({
        ...f,
        compositeResults: Array.isArray(f.compositeResults) ? [...f.compositeResults] : [],
      })),
      persistedPrintMap,
    );
    if (hydrateExisting) {
      forms = await hydrateGoodsFormsFromCompositeStore(id, forms);
    }

    const alreadyDone = countCompletedGroups(forms);
    const goodsImageUrlSnapshot = buildGoodsImageUrlSnapshot(persistedPrintMap);
    let lastProgressPersist = 0;

    const sequentialResult = await runGoodsFormsSequentialComposite({
      effectiveGoodsForms: forms,
      taskId: id,
      imageSpaceImages,
      goodsImageUrlSnapshot,
      fromCropTaskId,
      abortSignal,
      skipCompletedGroups: hydrateExisting && alreadyDone > 0,
      billingNav:
        billingNav
        || taskResultBase?.billingNavId
        || 'goods',
      onProgress: ({
        total: progressTotal,
        successCount: progressSuccess,
        failedCount: progressFailed,
        status: progressStatus,
        partialGoodsForms,
      }) => {
        const now = Date.now();
        const progressPayload = {
          total: progressTotal,
          successCount: progressSuccess,
          failedCount: progressFailed,
          status: progressStatus,
          partialGoodsForms,
          currentGroupIndex: progressSuccess + progressFailed,
        };
        publishGoodsTaskProgress(id, progressPayload);
        onLocalProgress?.(progressPayload);

        if (now - lastProgressPersist < 600) return;
        lastProgressPersist = now;
        taskAPI
          .update(id, {
            status: progressStatus || 'processing',
            total: progressTotal,
            successCount: progressSuccess,
            failedCount: progressFailed,
          })
          .catch(() => {});
      },
    });

    const {
      formsWithReferences,
      total,
      successCount,
      failedCount,
      finalStatus,
      failureHint,
      finishedAt,
    } = sequentialResult;

    const refsOnly = toRefsOnlyGoodsForms(
      applyGoodsPrintUrlsToForms(formsWithReferences, persistedPrintMap),
      id,
    );
    const finalPrintMap = buildPersistedGoodsPrintUrlMap(
      collectGoodsPrintUrlsFromForms(refsOnly, persistedPrintMap),
      lineageId,
    );
    const imageAssets = buildTaskImageAssetsPayload({
      existingAssets: taskResultBase?.imageAssets,
      printUrlByImageId: finalPrintMap,
      goodsForms: refsOnly,
      sourceTaskId: lineageId || id,
      sourceModule: billingNav || taskResultBase?.billingNavId || 'goods',
      includeComposites: true,
    });
    const printMapFromAssets = buildGoodsPrintUrlMapFromAssets(imageAssets);

    const resolvedBillingNav = billingNav
      || taskResultBase?.billingNavId
      || taskResultBase?.sourceNav
      || 'goods';
    const resolvedSourceNav = taskResultBase?.sourceNav || resolvedBillingNav;

    await taskAPI.update(id, {
      status: finalStatus,
      total,
      successCount,
      failedCount,
      finishedAt: finishedAt || format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      result: {
        ...taskResultBase,
        sourceNav: resolvedSourceNav,
        billingNavId: resolvedBillingNav,
        total,
        successCount,
        failedCount,
        failureHint: failureHint || undefined,
        goodsForms: refsOnly,
        goodsPrintUrlByImageId: {
          ...finalPrintMap,
          ...printMapFromAssets,
        },
        imageAssets,
      },
    });

    // 终态再推一次，避免详情页入场稍晚错过组级进度
    const finalProgress = {
      total,
      successCount,
      failedCount,
      status: finalStatus,
      partialGoodsForms: formsWithReferences,
      currentGroupIndex: successCount + failedCount,
    };
    publishGoodsTaskProgress(id, finalProgress);
    onLocalProgress?.(finalProgress);

    return { ...sequentialResult, refsOnly };
  };

  try {
    const slotModule = String(
      billingNav
      || taskResultBase?.billingNavId
      || taskResultBase?.sourceNav
      || 'goods',
    ).trim() || 'goods';
    const result = await withIpeProcessingSlot(
      id,
      slotModule,
      runCore,
      { isCancelled: () => Boolean(abortSignal?.aborted) },
    );
    if (result === undefined) {
      return null;
    }
    return result;
  } catch (error) {
    if (error?.message === '任务已终止' || abortSignal?.aborted) {
      throw error;
    }
    console.error('[主图创建] 后台处理失败', error);
    try {
      await taskAPI.update(id, {
        status: 'failed',
        result: {
          ...taskResultBase,
          failureHint: error?.message || '主图创建处理异常，请刷新详情页重试',
        },
      });
    } catch {
      /* ignore */
    }
    throw error;
  } finally {
    processingLocks.delete(id);
  }
}
